import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { FullPageOverlay } from '~/eds/src/components/FullPageOverlay/FullPageOverlay';
import { Select } from '~/eds/src/components/Select/Select';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Form } from '~/eds/src/modules/Form/Form';
import { StateUpdater } from '~/eds/src/modules/Form/types';
import { requiredValidator } from '~/eds/src/modules/Form/validators';
import { intlService } from '~/i18n/IntlService';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

type TicketFormValues = {
  name: string;
  workflowId: string | null;
};

type FormState<V> = {
  [K in keyof V]: { value: V[K]; disabled?: boolean };
};

const WorkflowCLMEvolutionTicketsNewPage = () => {
  const { navigate } = useRouting();
  const intl = useIntl();
  const [createTicket, { isLoading: isCreatingTicket }] =
    api.endpoints.createTicketK2.useMutation();
  const { data: workflowsData, isLoading: isLoadingWorkflows } =
    api.endpoints.listWorkflowsK2.useQuery({ published: true });

  const [formState, setFormState] = useState<FormState<TicketFormValues>>({
    name: { value: '' },
    workflowId: { value: null },
  });
  const [formErrors, setFormErrors] = useState<
    StateUpdater<TicketFormValues>['errors'] | undefined
  >(undefined);

  const workflowOptions = useMemo(() => {
    if (!workflowsData?.data) return [];
    return workflowsData.data.map((workflow) => ({
      value: workflow.id,
      label: workflow.workflow_definition?.name || `Workflow ${workflow.id}`,
    }));
  }, [workflowsData]);

  const fields = useMemo(
    () => ({
      name: {
        type: 'field' as const,
        name: 'name',
        label: 'Name',
        input: TextInput,
        required: true,
      },
      workflowId: {
        type: 'field' as const,
        name: 'workflowId',
        label: 'Workflow',
        input: Select,
        required: true,
        inputProps: {
          options: workflowOptions,
          isLoading: isLoadingWorkflows,
          isClearable: false,
          isSearchable: true,
          placeholder: 'Select a workflow',
        },
      },
    }),
    [workflowOptions, isLoadingWorkflows],
  );

  const handleCancel = () => {
    navigate(RoutePathType.WorkflowCLMEvolutionTickets, { replace: true });
  };

  const handleSubmit = async () => {
    try {
      const result = await createTicket({
        name: formState.name.value,
        workflowId: formState.workflowId.value as string,
      }).unwrap();

      if (result?.id) {
        navigate(
          RoutePathType.WorkflowCLMEvolutionTicketsDetail.replace(
            ':ticketId',
            result.id,
          ),
        );
      }
    } catch (error) {
      console.error('Failed to create ticket:', error);
    }
  };

  const handleOnChange = ({
    state,
    errors,
  }: StateUpdater<TicketFormValues>) => {
    setFormState(state);
    setFormErrors(
      errors && Object.keys(errors).length > 0 ? errors : undefined,
    );
  };

  const handleOnValidate = ({ errors }: StateUpdater<TicketFormValues>) => {
    setFormErrors(
      errors && Object.keys(errors).length > 0 ? errors : undefined,
    );
  };

  return (
    <FullPageOverlay
      title={intlService.getIntl().formatMessage({
        id: 'workflowCLMEvolutionTickets.newTicketTitle',
        defaultMessage: 'New Ticket',
        description: 'Title for new ticket overlay',
      })}
      actions={[
        {
          text: intlService.getIntl().formatMessage({
            id: 'workflowCLMEvolutionTickets.createTicketButton',
            defaultMessage: 'Create Ticket',
            description: 'Button text to create a new ticket',
          }),
          level: 'primary',
          disabled: isCreatingTicket || Boolean(formErrors),
          isLoading: isCreatingTicket,
          onClick: handleSubmit,
        },
      ]}
      cancelAction={{
        text: intlService.getIntl().formatMessage({
          id: 'workflowCLMEvolutionTickets.cancelButton',
          defaultMessage: 'Cancel',
          description: 'Cancel button text',
        }),
        level: 'tertiary',
        onClick: handleCancel,
      }}
    >
      <Form<TicketFormValues>
        fields={fields}
        state={formState}
        onChange={handleOnChange}
        onValidate={handleOnValidate}
        validators={[requiredValidator(['name', 'workflowId'], intl)]}
      />
    </FullPageOverlay>
  );
};

export default WorkflowCLMEvolutionTicketsNewPage;
